import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import SimpleSpinner from '@Components/Results/utils/SimpleSpinner';
import SynthesisPlannerConditionsComponent from '@Components/Results/synthesis-planner/synthesis-planner-conditions.component';
import StepSelectionHeader from '@Pages/RetroSynthesis/ProjectRoute/ProjectRouteConditions/components/StepSelectionHeader';
import cn from 'classnames';

import './StepDataConditions.theme.scss';

function StepDataConditions({
  isLoading,
  data,
  onFullTextLinkClick,
  onIssueCitedCounterClick,
  onExperimentalProcedureOpen,
  onAbstractOpen,
  onAuthorClick,
  stepId,
  saveStepSelectionsHandler,
  viewSelected,
}) {
  // sate saving of selected rows and control duplicated here to speedup user experience
  const [selectedRows, setSelectedRows] = useState([]);
  const [isViewOnlySelected, setViewOnlySelected] = useState(false);

  const saveSelection = (selected, selectedIds, trackingData = false) => {
    saveStepSelectionsHandler({
      stepId,
      selected,
      selectedIds,
      trackingData,
    });
  };

  const getConditions = (response) => {
    if (!response || !response.conditions || !response.conditions.length) {
      return [];
    }
    const preparation = response.conditions[0];
    const details = {};

    Object.keys(preparation).forEach((key) => {
      if (key !== 'step') {
        details.conditions = preparation[key];
        details.reactionId = key;
      }
    });

    return details.conditions.map((d, i) => ({ ...d, idN: `${details.reactionId}-${i}`, index: i + 1 }));
  };

  const handleConditionSelection = (id, checked) => {
    let rows;
    let trackingData = null;
    if (checked) {
      rows = [...selectedRows, id];
      trackingData = {
        selectedResults: getConditions(data)
          .filter((c) => c.id === id)
          .map((c) => c.index)
          .join(','),
      };
    } else {
      rows = selectedRows.filter((v) => v !== id);
    }
    setSelectedRows(rows);
    const viewOnlySelected = rows.length === 0 ? false : isViewOnlySelected;
    if (!viewOnlySelected) {
      setViewOnlySelected(false);
    }
    saveSelection(viewOnlySelected, rows, trackingData);
  };

  const isConditionChecked = (id) => selectedRows.includes(id);

  const isConditionVisible = (id) => {
    if (isViewOnlySelected) {
      return selectedRows.includes(id);
    }
    return true;
  };

  const selectAllHandler = (selected) => {
    let rows;
    let trackingData = null;
    if (selected) {
      rows = getConditions(data).map((c) => c.id);
      trackingData = { selectedResults: 'all' };
    } else {
      rows = [];
    }
    setSelectedRows(rows);
    const viewOnlySelected = rows.length === 0 ? false : isViewOnlySelected;
    if (!viewOnlySelected) {
      setViewOnlySelected(false);
    }
    saveSelection(viewOnlySelected, rows, trackingData);
  };

  const handleViewOnlySelection = (e, checked) => {
    setViewOnlySelected(checked);
    saveSelection(checked, selectedRows, { viewSelected: true });
  };

  useEffect(() => {
    if (getConditions(data).length) {
      setSelectedRows(
        getConditions(data)
          .filter((e) => e.selected)
          .map((c) => c.id)
      );
    }
  }, [data]);

  useEffect(() => {
    setViewOnlySelected(viewSelected);
  }, [viewSelected]);

  return (
    <>
      <StepSelectionHeader
        selectedRowsCount={selectedRows.length}
        selectAllHandler={selectAllHandler}
        isViewOnlySelected={isViewOnlySelected}
        setViewOnlySelected={handleViewOnlySelection}
        maxRowsCount={getConditions(data).length}
      />
      <div
        className={cn(
          'project-route-step-data',
          'project-route-step-data-conditions',
          'project-route-step-data-conditions--with-checkboxes'
        )}>
        {isLoading && <SimpleSpinner />}
        {data && (
          <SynthesisPlannerConditionsComponent
            data={data}
            showSubHeader={false}
            onFullTextLinkClick={onFullTextLinkClick}
            onIssueCitedCounterClick={onIssueCitedCounterClick}
            onExperimentalProcedureOpen={onExperimentalProcedureOpen}
            onAbstractOpen={onAbstractOpen}
            onAuthorClick={onAuthorClick}
            handleConditionSelection={handleConditionSelection}
            isConditionCheckedF={isConditionChecked}
            isConditionVisibleF={isConditionVisible}
            renderCheckboxesColumn
          />
        )}
      </div>
    </>
  );
}

StepDataConditions.defaultProps = {
  onExperimentalProcedureOpen: () => null,
  onFullTextLinkClick: () => null,
  onIssueCitedCounterClick: () => null,
  onAbstractOpen: () => null,
  onAuthorClick: () => null,
};

StepDataConditions.propTypes = {
  isLoading: PropTypes.bool,
  data: PropTypes.object,
  onExperimentalProcedureOpen: PropTypes.func,
  onFullTextLinkClick: PropTypes.func,
  onIssueCitedCounterClick: PropTypes.func,
  onAbstractOpen: PropTypes.func,
  onAuthorClick: PropTypes.func,
  saveStepSelectionsHandler: PropTypes.func,
  stepId: PropTypes.string,
  viewSelected: PropTypes.bool,
};

export default StepDataConditions;
